// ⚠️⚠️ TOOLS → HTML HOSTING — THE SANDBOX POLICY. ONE DEFINITION, TWO
// CONSUMERS. READ THIS BEFORE CHANGING A SINGLE TOKEN.
//
// An uploaded HTML report is CODE, and we serve it from our own origin. If it
// ran AS our origin its scripts would reach `localStorage`, where Firebase Auth
// keeps a signed-in staff member's session token. We cannot sanitise our way
// out (DOMPurify strips the <script> the charts need — see publicRouter.ts), so
// the ONLY protection is that the document lands in a unique OPAQUE ORIGIN.
//
// ⚠️⚠️ `allow-same-origin` RE-JOINS IT TO US AND REMOVES THAT PROTECTION
// ENTIRELY. It is one token, there is no error, and every report keeps
// rendering identically. It is listed in FORBIDDEN_SANDBOX_TOKENS below and a
// test asserts the two lists are disjoint, so granting it fails the suite at
// the definition site as well as at the served header.
//
// WHY THIS FILE EXISTS AT ALL: the policy has TWO consumers that must agree —
// the `Content-Security-Policy` response header on the /content route (which is
// the ENFORCEMENT) and the `sandbox` attribute on the viewer's iframe (which is
// belt-and-braces on top of it). The effective restriction is the UNION of the
// two, so an iframe attribute cannot grant what the header withholds: relaxing
// one alone changes nothing and looks like a bug in the browser. Two
// hand-maintained token lists in two files would drift, so there is one list
// here and both sides DERIVE from it.
//
// Pure data + pure predicates, deliberately free of Vite and Node specifics so
// the server imports it directly — the same posture as src/lib/allowedEmails.ts.

/**
 * The sandbox tokens a hosted report is granted.
 *
 * `allow-scripts` — the charts are JavaScript; without it the feature does not
 *   work at all. This is what the opaque origin exists to make safe.
 * `allow-popups` — a report's outbound links (`target="_blank"`,
 *   `window.open`) are otherwise blocked SILENTLY, with nothing in the page to
 *   say why. Added 2026-09-08 on Scott's ask.
 * `allow-popups-to-escape-sandbox` — the opened tab does NOT inherit the
 *   sandbox. ⚠️ LOAD-BEARING, NOT A NICETY, and shipping `allow-popups`
 *   without it would be worse than the blocked link it replaced: a sandboxed
 *   popup loads the destination in an opaque origin TOO, so a link to the
 *   client's own site would open with no cookies, no session and no
 *   localStorage ON THEIR OWN DOMAIN. The link would "work" and the
 *   destination would then misbehave, which is a far more confusing bug.
 *
 * ⚠️ WHAT THIS COSTS, recorded rather than implied away: a malicious uploaded
 * report can now open a full-privilege tab, or a popunder. Accepted, because
 * uploads are AUTHED STAFF ONLY and the threat model is "a compromised report
 * generator, or HTML pasted from elsewhere" — an ordinary link in a report can
 * already send someone somewhere hostile. ⚠️ THE CROWN JEWEL IS UNTOUCHED: the
 * report document itself still lands in a unique opaque origin and still cannot
 * reach our cookies, localStorage or IndexedDB.
 */
export const REPORT_SANDBOX_TOKENS = [
  'allow-scripts',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
] as const;

/**
 * Tokens that must NEVER be granted, asserted rather than remembered.
 *
 * `allow-same-origin` is the catastrophic one (see the header). The rest are
 * ordinary hygiene: a report is a document to read, so it has no business
 * navigating the top-level window it is embedded in, submitting forms, locking
 * the pointer or casting to a presentation.
 *
 * ⚠️ TOP NAVIGATION STAYS FORBIDDEN EVEN THOUGH POPUPS NOW DO NOT, and the two
 * are not the same permission: a popup opens a NEW tab the reader can close,
 * while top navigation replaces the page they were reading — which for an
 * embedded document means navigating OUR viewer away. There is no report that
 * needs that.
 *
 * ⚠️ `allow-downloads` is absent from BOTH lists deliberately — it is neither
 * granted nor asserted-forbidden, because a report offering a CSV export is a
 * plausible future ask and blocking it here would read as a security rule when
 * it is really an unmade decision.
 */
export const FORBIDDEN_SANDBOX_TOKENS = [
  'allow-same-origin',
  'allow-top-navigation',
  'allow-top-navigation-by-user-activation',
  'allow-top-navigation-to-custom-protocols',
  'allow-forms',
  'allow-modals',
  'allow-pointer-lock',
  'allow-presentation',
  'allow-storage-access-by-user-activation',
] as const;

/** The value for the viewer iframe's `sandbox` attribute. */
export const REPORT_SANDBOX_ATTRIBUTE: string = REPORT_SANDBOX_TOKENS.join(' ');

/**
 * The served document's policy — the actual enforcement.
 *
 * `frame-ancestors 'self'` is additive belt-and-braces: our viewer page embeds
 * the report in an iframe, and this stops any OTHER site embedding it to
 * clickjack a client's report. ⚠️ It must survive every future edit to the
 * token list; it is asserted separately for that reason.
 */
export const REPORT_SANDBOX_CSP: string =
  `sandbox ${REPORT_SANDBOX_ATTRIBUTE}; frame-ancestors 'self'`;

/**
 * The `sandbox` directive's tokens, or `null` when the directive is ABSENT.
 *
 * ⚠️⚠️ THE NULL IS THE POINT, AND COLLAPSING IT TO `[]` WOULD INVERT THE
 * MEANING. A `sandbox` directive carrying NO tokens is the MOST restrictive
 * sandbox there is (everything denied); a MISSING `sandbox` directive is NO
 * SANDBOX AT ALL. They are opposites, so "the directive was dropped" cannot be
 * detected by looking at tokens — it needs this null.
 */
export function sandboxDirective(csp: string): string[] | null {
  for (const raw of csp.split(';')) {
    const parts = raw.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) continue;
    if (parts[0]!.toLowerCase() === 'sandbox') return parts.slice(1);
  }
  return null;
}

/**
 * Whether a policy grants a sandbox token — matched on TOKEN BOUNDARIES, over
 * the whole header.
 *
 * ⚠️⚠️ A SUBSTRING CHECK IS WRONG IN BOTH DIRECTIONS HERE, and that is not
 * hypothetical: `allow-popups-to-escape-sandbox` CONTAINS `allow-popups`, so
 * `includes('allow-popups')` reports the bare token as granted when it is not,
 * and a `not.includes('allow-popups')` guard fails the moment the longer token
 * is legitimately added — at which point the tempting "fix" is to delete the
 * guard. Splitting into whole tokens distinguishes them.
 *
 * Scanned over the ENTIRE header rather than the first `sandbox` directive, so
 * a token smuggled into a second directive is still caught. Directive names and
 * values are in the split too, which is harmless — no sandbox token is spelled
 * like a directive name.
 *
 * ⚠️ Deliberately a SPLIT rather than a constructed `new RegExp`: a boundary
 * class written inside a template literal (`[\s;]`) silently loses its
 * backslash and becomes `[s;]`, so whitespace stops being a boundary and the
 * function returns false for every genuinely granted token — which would make
 * every forbidden-token assertion built on it pass vacuously. That is not a
 * hypothetical either; it happened while writing this file. A regex LITERAL has
 * no such trap, and this form needs no escaping at all.
 */
export function grantsSandboxToken(csp: string, token: string): boolean {
  return csp
    .split(/[\s;]+/)
    .filter(Boolean)
    .includes(token);
}
